import { ReactNode } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';
import {
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

const infoBoxVariants = cva('p-4 rounded-lg border flex items-start gap-3', {
  variants: {
    variant: {
      info: 'bg-info/10 border-info/20 text-info',
      success: 'bg-success/10 border-success/20 text-success',
      warning: 'bg-warning/10 border-warning/20 text-warning',
      error: 'bg-destructive/10 border-destructive/20 text-destructive',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

const iconMap = {
  info: InformationCircleIcon,
  success: CheckCircleIcon,
  warning: ExclamationTriangleIcon,
  error: XCircleIcon,
};

export interface InfoBoxProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof infoBoxVariants> {
  children: ReactNode;
  icon?: ReactNode;
  showIcon?: boolean;
}

export function InfoBox({
  children,
  className,
  variant = 'info',
  icon,
  showIcon = true,
  ...props
}: InfoBoxProps) {
  const Icon = iconMap[variant || 'info'];

  return (
    <div className={cn(infoBoxVariants({ variant }), className)} {...props}>
      {showIcon && (icon || <Icon className="h-5 w-5 flex-shrink-0 mt-0.5" />)}
      <div className="flex-1 text-sm text-foreground">{children}</div>
    </div>
  );
}
